import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CategoryData } from '../../data';
import {
    Box,
    Card,
    Checkbox,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import Scrollbar from '../Scrollbar';

interface CategoryListTableProps {
    categories: CategoryData[];
}

const applyPagination = (categories: CategoryData[], page: number, limit: number): CategoryData[] =>
    categories.slice(page * limit, page * limit + limit);

export const CategoryListTable = (props: CategoryListTableProps) => {
    const { categories, ...other } = props;
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [page, setPage] = useState<number>(0);
    const [limit, setLimit] = useState<number>(10);

    const handleSelectAllCategories = (event: ChangeEvent<HTMLInputElement>): void => {
        setSelectedCategories(event.target.checked ? categories.map((category) => category.categoryId) : []);
    };

    const handleSelectOneCategory = (event: ChangeEvent<HTMLInputElement>, categoryId: number): void => {
        if (!selectedCategories.includes(categoryId)) {
            setSelectedCategories((prevSelected) => [...prevSelected, categoryId]);
        } else {
            setSelectedCategories((prevSelected) => prevSelected.filter((id) => id !== categoryId));
        }
    };

    const handlePageChange = (event: any, newPage: number): void => {
        setPage(newPage);
    };

    const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedCategories = applyPagination(categories, page, limit);
    const selectedSomeCategories = selectedCategories.length > 0 && selectedCategories.length < categories.length;
    const selectedAllCategories = selectedCategories.length === categories.length;

    return (
        <Card {...other}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    m: -1,
                    p: 2,
                }}
            ></Box>
            <Scrollbar>
                <Box sx={{ minWidth: 700 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedAllCategories}
                                        color="primary"
                                        indeterminate={selectedSomeCategories}
                                        onChange={handleSelectAllCategories}
                                    />
                                </TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Articles</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCategories.map((category) => {
                                const isCategorySelected = selectedCategories.includes(category.categoryId);

                                return (
                                    <TableRow hover key={category.categoryId} selected={isCategorySelected}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isCategorySelected}
                                                color="primary"
                                                onChange={(event) =>
                                                    handleSelectOneCategory(event, category.categoryId)
                                                }
                                                value={isCategorySelected}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Box>
                                                    <Link
                                                        color="inherit"
                                                        component={RouterLink}
                                                        to={`/kb/categories/${category.categoryId}`}
                                                    >
                                                        {category.name}
                                                    </Link>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{category.questionCount}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Scrollbar>
            <TablePagination
                component="div"
                count={categories.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={page}
                rowsPerPage={limit}
                rowsPerPageOptions={[5, 10, 25]}
            />
        </Card>
    );
};
